import axios from 'axios';

import type { GetPublicSettings200Response } from '@nora-monorepo/nora-api-client';

import { useApiStore } from '#ui-shared/stores/api/api-store';
import { useConfigStore } from '#ui-shared/stores/config-store';
import { useGenericAppStore } from '#ui-shared/stores/generic-app-store';
import { useWorkspaceStore } from '#ui-shared/stores/workspace-store';

export async function bootstrapSettings(
  serverUrl?: string | null,
): Promise<void> {
  const configStore = useConfigStore();
  const apiStore = useApiStore();
  const workspaceStore = useWorkspaceStore();
  const genericAppStore = useGenericAppStore();

  const handleMissingSettings = (): void => {
    if (serverUrl) {
      workspaceStore.selectedWorkspaceUrl = null;
    }
  };

  const response = await apiStore.unwrap(apiStore.settings.getFrontendSettingsV2());

  if (!response.data) {
    handleMissingSettings();

    throw new Error('No settings data returned');
  }

  const settings = response.data;

  if (workspaceStore.selectedWorkspace) {
    workspaceStore.selectedWorkspace.name
      = settings.general.workspaceTitle ?? 'workspace';
  }
  else {
    workspaceStore.addWorkspace({
      serverUrl: configStore.baseUrl,
      name: settings.general.workspaceTitle ?? 'workspace',
    });
  }

  if (settings.general.title) {
    document.title = settings.general.title;
  }

  genericAppStore.setShowBetaNotice(
    settings.general.showBetaNotice ?? false,
  );
  configStore.setAppConfig(settings);
}

export async function bootstrapUserSettings(): Promise<void> {
  const configStore = useConfigStore();
  const api = useApiStore();

  const request = await api.unwrap(api.settings.getUserSettings());
  const data = request.data;

  if (!data) {
    throw new Error('User settings was not returned.');
  }

  configStore.setUserSettings(data);
}

const PUBLIC_SETTINGS_RETRY_INTERVAL_MS = 3000;
const PUBLIC_SETTINGS_MAX_ATTEMPTS = 10;

export async function bootstrapPublicSettings(): Promise<void> {
  const configStore = useConfigStore();

  const configPath = '/api/nora/settings/public';
  const url = `${configStore.baseUrl}${configPath}`;

  // This used to retry forever, stranding the user on the splash screen with no error whenever
  // nothing could supply the settings. Giving up lets the bootstrap store fail.
  for (let attempt = 1; attempt <= PUBLIC_SETTINGS_MAX_ATTEMPTS; attempt += 1) {
    try {
      const { data: response }
        = await axios.get<GetPublicSettings200Response>(url);

      if (!response.data) {
        throw new Error('No public settings data returned');
      }

      const settings = response.data;

      configStore.setPublicConfig(settings);

      return;
    }
    catch (error) {
      if (attempt >= PUBLIC_SETTINGS_MAX_ATTEMPTS) {
        console.error(
          `Error fetching public settings, giving up after ${attempt} attempts`,
          error,
        );

        throw error;
      }

      console.error(
        `Error fetching public settings (attempt ${attempt}/${PUBLIC_SETTINGS_MAX_ATTEMPTS}), will retry in ${PUBLIC_SETTINGS_RETRY_INTERVAL_MS}ms`,
        error,
      );

      await new Promise(resolve => setTimeout(resolve, PUBLIC_SETTINGS_RETRY_INTERVAL_MS));
    }
  }
}
